import { fetchApiVersion } from "./api";

type VersionPollerOptions = {
  intervalMs?: number;
  fetchVersion?: typeof fetchApiVersion;
  reload?: () => void;
  logError?: (err: unknown) => void;
  readStoredVersion?: () => string | undefined;
  writeStoredVersion?: (version: string) => void;
};

const DEFAULT_INTERVAL_MS = 40_000; // 40 seconds
const VERSION_STORAGE_KEY = "prophet.app_version";

type LocationLike = {
  href: string;
  replace: (url: string) => void;
};

function readStoredVersion(): string | undefined {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return undefined;
  }
  try {
    const value = window.localStorage.getItem(VERSION_STORAGE_KEY);
    return value ?? undefined;
  } catch {
    return undefined;
  }
}

function writeStoredVersion(version: string): void {
  if (typeof window === "undefined" || typeof window.localStorage === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(VERSION_STORAGE_KEY, version);
  } catch {
    // ignored if browser blocks localStorage
  }
}

export function hardReload(locationValue: LocationLike = window.location, now: () => number = Date.now): void {
  const url = new URL(locationValue.href);
  url.searchParams.set("vcheck", String(now()));
  locationValue.replace(url.toString());
}

export function createVersionPoller(options: VersionPollerOptions = {}) {
  const fetchVersion = options.fetchVersion ?? fetchApiVersion;
  const reload = options.reload ?? (() => hardReload());
  const logError = options.logError ?? ((err: unknown) => console.error(err));
  const readVersion = options.readStoredVersion ?? readStoredVersion;
  const writeVersion = options.writeStoredVersion ?? writeStoredVersion;
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;

  let currentVersion: string | undefined;
  let timer: number | undefined;

  const checkNow = async () => {
    try {
      const { version } = await fetchVersion();
      const previousVersion = currentVersion ?? readVersion();
      if (previousVersion && version && version !== previousVersion) {
        currentVersion = version;
        writeVersion(version);
        reload();
        return;
      }
      if (version) {
        currentVersion = version;
        writeVersion(version);
      }
    } catch (err) {
      logError(err);
    }
  };

  const start = () => {
    void checkNow();
    timer = window.setInterval(checkNow, intervalMs);
    return () => {
      if (timer !== undefined) {
        window.clearInterval(timer);
      }
    };
  };

  return { start, checkNow };
}

export async function checkVersionImmediately() {
  const poller = createVersionPoller();
  await poller.checkNow();
}
